import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowDown, ArrowUp, ChevronRight, Inbox, Loader2, X } from "lucide-react";
import { heroPortraitUrl } from "../api";
import { useCatalog } from "../context/Catalog";

export const ROLE_COLOR = { Vanguard: "#4C8DF5", Duelist: "#EF5350", Strategist: "#27AE60", Flex: "#A0A0A0", Coach: "#E6A93C", Sub: "#666666" };
export const nfmt = (value, digits = 0) =>
  value == null || Number.isNaN(value) ? "—" : new Intl.NumberFormat(undefined, { maximumFractionDigits: digits }).format(value);
export const pct = (value) => (value == null ? "—" : `${nfmt(value, 1)}%`);

// ----------------------------------------------------------------------------- layout
// Shapes follow rivals-stats2.0: Bebas Neue headings, #141414 panels, label-style eyebrows.

export function Page({ eyebrow, title, subtitle, actions, children, back }) {
  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 sm:px-6 lg:px-8 py-5 fade-up">
      {back}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
        <div className="min-w-0">
          {eyebrow && <span className="label">{eyebrow}</span>}
          <h1 className="font-display text-4xl tracking-tight leading-none mt-1 break-words">{title}</h1>
          {subtitle && <p className="text-xs text-[#888] mt-1.5 max-w-2xl">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-end gap-2">{actions}</div>}
      </div>
      <div className="space-y-5 min-w-0">{children}</div>
    </div>
  );
}

/** Panel: title in Bebas, optional sub line (the old `eyebrow` prop) underneath. */
export function Panel({ title, eyebrow, sub, actions, children, className = "", pad = true }) {
  const note = sub ?? eyebrow;
  return (
    <section className={`bg-[#141414] border border-white/10 min-w-0 ${pad ? "p-5" : ""} ${className}`}>
      {(title || note || actions) && (
        <div className={`flex items-start justify-between gap-4 flex-wrap mb-4 ${pad ? "" : "px-5 pt-5"}`}>
          <div className="min-w-0">
            {title && <h2 className="font-display text-2xl tracking-tight">{title}</h2>}
            {note && <p className="text-xs text-[#777] mt-1">{note}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/** 2.0's section header: green eyebrow ("Teamfights / Fight stats"), big title, sub, rule. */
export function SectionTitle({ eyebrow, title, sub, badge }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 pb-3 border-b border-white/10">
      <div>
        {eyebrow && <div className="label mb-1 !text-[#27AE60]">{eyebrow}</div>}
        <h2 className="font-display text-3xl tracking-tight leading-none">{title}</h2>
        {sub && <p className="text-xs text-[#888] mt-1">{sub}</p>}
      </div>
      {badge && <span className="label px-3 py-1.5 border border-[#27AE60]/40 bg-[#27AE60]/10 !text-[#27AE60]">{badge}</span>}
    </div>
  );
}

export function Grid({ cols = 2, children, className = "" }) {
  const map = { 2: "xl:grid-cols-2", 3: "xl:grid-cols-3", 4: "md:grid-cols-2 xl:grid-cols-4" };
  // min-w-0 lets wide tables scroll inside their panel instead of stretching the page.
  return <div className={`grid grid-cols-1 gap-6 [&>*]:min-w-0 ${map[cols] || ""} ${className}`}>{children}</div>;
}

const TONE = { good: "#00ff66", bad: "#fa4454", warn: "#f5c451", accent: "#27AE60" };

/** 2.0 stat card: label (and icon) on top, big Bebas value, small sub line. */
export function Kpi({ label, value, sub, tone, color, icon: Icon }) {
  const c = color || TONE[tone];
  return (
    <div className="bg-[#141414] border border-white/10 p-5 min-w-0">
      <div className="flex items-center justify-between gap-2 mb-4">
        <span className="label">{label}</span>
        {Icon && <Icon className="w-5 h-5 shrink-0" style={{ color: c || "#27AE60" }} />}
      </div>
      <div className="font-display text-4xl leading-none truncate" style={c ? { color: c } : undefined}>{value ?? "—"}</div>
      {sub && <div className="text-xs text-[#777] mt-2 truncate">{sub}</div>}
    </div>
  );
}

export function KpiRow({ children, cols = 4 }) {
  const wide = { 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-3 xl:grid-cols-6" }[cols];
  return <div className={`grid grid-cols-1 sm:grid-cols-2 ${wide} gap-4 [&>*]:min-w-0`}>{children}</div>;
}

/** Click-to-open menu: closes on outside click, Escape, or picking an item. */
export function Dropdown({ trigger, children, align = "left", className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <div ref={ref} className={`relative ${className}`}>
      {trigger({ open, toggle: () => setOpen((o) => !o) })}
      {open && (
        <div className={`absolute top-full z-50 mt-1 min-w-[200px] border border-white/10 bg-[#0d0d0d] py-1 shadow-2xl ${align === "right" ? "right-0" : "left-0"}`}
          onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Tabs in the two 2.0 styles. "page" (default): the profile tab row, every tab inline.
 * "toggle": the green segmented control used inside panels and filter bars.
 */
export function Tabs({ tabs, value, onChange, variant = "page" }) {
  if (variant === "toggle") {
    return (
      <div className="inline-flex flex-wrap border border-white/10 bg-black/20 p-1 text-xs font-bold uppercase tracking-widest" role="tablist">
        {tabs.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={value === id} onClick={() => onChange(id)}
            className={`px-3 py-2 transition-colors ${value === id ? "bg-[#27AE60] text-black" : "text-[#999] hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>
    );
  }
  return (
    <nav className="flex gap-1 border border-white/10 bg-[#101010] px-2 overflow-x-auto" role="tablist" aria-label="Sections">
      {tabs.map(([id, label]) => (
        <button key={id} type="button" role="tab" aria-selected={value === id} onClick={() => onChange(id)}
          className={`px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${value === id ? "border-[#27AE60] bg-white/[0.03] text-white" : "border-transparent text-[#888] hover:bg-white/[0.02] hover:text-white"}`}>
          {label}
        </button>
      ))}
    </nav>
  );
}

// ----------------------------------------------------------------------------- states
export function Loading({ text = "Loading…" }) {
  return (
    <div className="min-h-[30vh] grid place-items-center text-muted">
      <span className="inline-flex items-center gap-2 text-sm"><Loader2 size={16} className="spin" /> {text}</span>
    </div>
  );
}

export function ErrorBox({ error, onRetry }) {
  if (!error) return null;
  return (
    <div className="border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger flex items-center gap-3">
      <AlertTriangle size={16} className="shrink-0" />
      <span className="flex-1">{error}</span>
      {onRetry && <button className="underline" onClick={onRetry}>Retry</button>}
    </div>
  );
}

export function Empty({ title = "Nothing here yet", text, action }) {
  return (
    <div className="border border-dashed border-white/10 bg-black/10 px-6 py-10 text-center">
      <Inbox className="mx-auto text-[#666] mb-3" size={22} />
      <p className="font-display text-2xl tracking-tight">{title}</p>
      {text && <p className="text-sm text-[#666] mt-2 max-w-md mx-auto">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Standard loading / error wrapper for a useLoad() result. */
export function Async({ state, children, empty }) {
  if (state.loading && !state.data) return <Loading />;
  if (state.error && !state.data) return <ErrorBox error={state.error} onRetry={() => state.reload()} />;
  if (!state.data) return null;
  if (empty && empty(state.data)) return <Empty title="No data for these filters" text="Import replays or widen the filters." />;
  return children(state.data);
}

// ----------------------------------------------------------------------------- badges & links
export function ResultBadge({ result }) {
  const style = { win: "bg-[#00ff66]/15 text-[#00ff66]", loss: "bg-[#ff2a2a]/15 text-[#ff6b6b]" }[result] || "bg-white/10 text-[#a0a0a0]";
  return <span className={`inline-block text-xs font-bold px-2 py-1 ${style}`}>{result === "win" ? "Win" : result === "loss" ? "Loss" : "Draw"}</span>;
}

/** 2.0 win-rate colours: green from 60%, yellow from 50%, red below. */
export const wrColor = (value) => (Number(value || 0) >= 60 ? "#00ff66" : Number(value || 0) >= 50 ? "#f5c451" : "#fa4454");

export function WinRate({ value, maps }) {
  const color = wrColor(value);
  return (
    <span className="inline-flex items-center gap-2 min-w-[92px]">
      <span className="h-1.5 w-12 bg-white/10 overflow-hidden"><span className="block h-full" style={{ width: `${Math.min(100, value || 0)}%`, background: color }} /></span>
      <span className="font-display text-base tnum" style={{ color }}>{pct(value)}</span>
      {maps != null && <span className="text-[10px] text-[#777]">({maps})</span>}
    </span>
  );
}

export function Badge({ children, color }) {
  return <span className="inline-block border border-white/10 px-2 py-1 text-[0.65rem] font-bold uppercase tracking-widest" style={color ? { color, borderColor: `${color}66`, background: `${color}14` } : undefined}>{children}</span>;
}

export function HeroIcon({ hero, id, size = 32, className = "" }) {
  const { hero: lookup } = useCatalog();
  const [failed, setFailed] = useState(false);
  const meta = lookup(hero);
  const portrait = id ?? meta?.portrait_id ?? null;
  const namedPortrait = {
    "the hood": "/heroes/transformations/the-hood-headbig-v2.png",
    gorr: "/heroes/transformations/gorr-headbig-v2.png",
  }[String(hero || "").trim().toLowerCase()];
  const portraitSrc = namedPortrait || (portrait ? heroPortraitUrl(portrait) : null);
  const initials = String(hero || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2);
  useEffect(() => setFailed(false), [portraitSrc]);
  return (
    <span className={`relative inline-grid shrink-0 place-items-center overflow-hidden border border-border bg-panel2 text-[10px] font-black text-muted ${className}`} style={{ width: size, height: size }} title={hero}>
      <span>{initials}</span>
      {portraitSrc && !failed && (
        <img src={portraitSrc} alt={hero} loading="lazy" onError={() => setFailed(true)} className="absolute inset-0 h-full w-full object-cover object-top" />
      )}
    </span>
  );
}

export function HeroChip({ hero, sub, link = true, size = 28 }) {
  const content = (
    <span className="inline-flex items-center gap-2 min-w-0">
      <HeroIcon hero={hero} size={size} />
      <span className="min-w-0">
        <span className="block truncate font-semibold text-[13px]">{hero}</span>
        {sub && <span className="block text-[11px] text-subtle truncate">{sub}</span>}
      </span>
    </span>
  );
  return link && hero ? <Link className="hover:text-accent" to={`/heroes/${encodeURIComponent(hero)}`}>{content}</Link> : content;
}

export function HeroStack({ heroes = [], size = 26 }) {
  return <span className="inline-flex flex-wrap gap-1">{heroes.map((h) => <HeroIcon key={typeof h === "string" ? h : h.hero} hero={typeof h === "string" ? h : h.hero} size={size} />)}</span>;
}

export const PlayerLink = ({ playerKey, name }) =>
  playerKey ? <Link className="font-semibold hover:text-accent" to={`/players/${encodeURIComponent(name || playerKey)}`}>{name}</Link> : <span>{name}</span>;

export const TeamLink = ({ id, name }) =>
  id ? <Link className="font-semibold hover:text-accent" to={`/teams/${encodeURIComponent(name || id)}`}>{name}</Link> : <span className="text-muted">{name || "—"}</span>;

// ----------------------------------------------------------------------------- table
/**
 * columns: [{ key, label, render(row), value(row) for sorting, align, className }]
 */
export function DataTable({ columns, rows, rowKey = (r, i) => i, initialSort, empty = "No rows.", onRowClick, renderExpanded, limit }) {
  const [sort, setSort] = useState(initialSort || null);
  const [showAll, setShowAll] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const sorted = useMemo(() => {
    if (!sort) return rows || [];
    const column = columns.find((c) => c.key === sort.key);
    const value = column?.value || ((r) => r[sort.key]);
    return [...(rows || [])].sort((a, b) => {
      const x = value(a), y = value(b);
      const cmp = typeof x === "string" || typeof y === "string" ? String(x ?? "").localeCompare(String(y ?? "")) : (x ?? -Infinity) - (y ?? -Infinity);
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort, columns]);
  if (!rows?.length) return <p className="text-sm text-subtle py-3">{empty}</p>;
  const visible = limit && !showAll ? sorted.slice(0, limit) : sorted;
  const toggle = (key) => setSort((s) => (s?.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm tnum">
        <thead>
          <tr className="border-b border-white/10">
            {columns.map((c) => (
              <th key={c.key} className={`label py-3 px-3 first:pl-0 whitespace-nowrap ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"}`}>
                {c.sortable === false ? c.label : (
                  <button className="inline-flex items-center gap-1 hover:text-white uppercase tracking-[0.2em]" onClick={() => toggle(c.key)}>
                    {c.label}
                    {sort?.key === c.key && (sort.dir === "desc" ? <ArrowDown size={11} /> : <ArrowUp size={11} />)}
                  </button>
                )}
              </th>
            ))}
            {renderExpanded && <th className="w-10 py-3 px-2"><span className="sr-only">Details</span></th>}
          </tr>
        </thead>
        <tbody>
          {visible.map((row, i) => {
            const key = rowKey(row, i);
            const expanded = expandedKey === key;
            const toggleExpanded = () => setExpandedKey((current) => current === key ? null : key);
            return (
              <React.Fragment key={key}>
                <tr aria-expanded={renderExpanded ? expanded : undefined}
                  onClick={(event) => {
                    if (event.target.closest("a, button, input, select, textarea")) return;
                    if (onRowClick) onRowClick(row);
                    if (renderExpanded) toggleExpanded();
                  }}
                  className={`border-b border-white/5 hover:bg-white/[0.03] ${onRowClick || renderExpanded ? "cursor-pointer" : ""}`}>
                  {columns.map((c) => (
                    <td key={c.key} className={`py-3 px-3 first:pl-0 ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : ""} ${c.className || ""}`}>
                      {c.render ? c.render(row) : row[c.key] ?? "—"}
                    </td>
                  ))}
                  {renderExpanded && (
                    <td className="py-3 px-2 text-right">
                      <button type="button" onClick={toggleExpanded} aria-label={expanded ? "Hide details" : "Show details"}
                        className="p-1 text-[#777] hover:text-white">
                        <ChevronRight size={16} className={`transition-transform ${expanded ? "rotate-90" : ""}`} />
                      </button>
                    </td>
                  )}
                </tr>
                {renderExpanded && expanded && (
                  <tr className="border-b border-white/10 bg-black/20">
                    <td colSpan={columns.length + 1} className="p-4 sm:p-5">{renderExpanded(row)}</td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {limit && sorted.length > limit && (
        <button className="mt-3 text-xs font-bold uppercase tracking-widest text-[#777] hover:text-white" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Show less" : `Show all ${sorted.length}`}
        </button>
      )}
    </div>
  );
}

/** Horizontal bar list for counts/shares. rows: [{ label, value, sub, color }] */
export function BarList({ rows, format = (v) => nfmt(v), max }) {
  if (!rows?.length) return <p className="text-sm text-subtle">No data.</p>;
  const top = max ?? Math.max(...rows.map((r) => r.value || 0), 1);
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => (
        <div key={r.key ?? r.label ?? i}>
          <div className="flex items-center justify-between gap-3 text-[13px] mb-1">
            <span className="min-w-0 truncate">{r.label}</span>
            <span className="mono text-xs text-muted shrink-0">{format(r.value)}{r.sub && <span className="text-subtle"> · {r.sub}</span>}</span>
          </div>
          <div className="h-1.5 bg-white/10"><div className="h-full" style={{ width: `${(100 * (r.value || 0)) / top}%`, background: r.color || "#27AE60" }} /></div>
        </div>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------------- forms
export function Modal({ title, onClose, children, footer, wide }) {
  useEffect(() => {
    const close = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);
  if (typeof document === "undefined") return null;
  return createPortal((
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/75 p-3 sm:p-5" onMouseDown={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.stopPropagation()}
        className={`flex w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-2.5rem)] flex-col overflow-hidden border border-white/10 bg-[#141414] shadow-lift`}>
        <div className="z-10 flex shrink-0 items-center justify-between border-b border-white/10 bg-[#141414] px-5 py-4">
          <h2 className="font-display text-3xl tracking-tight">{title}</h2>
          <button onClick={onClose} className="p-1 text-muted hover:text-fg" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5 space-y-4 [scrollbar-gutter:stable]">{children}</div>
        {footer && <div className="z-10 flex shrink-0 justify-end gap-2 border-t border-white/10 bg-[#141414] px-5 py-3">{footer}</div>}
      </div>
    </div>
  ), document.body);
}

export function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="label block mb-2">{label}</span>
      {children}
      {hint && <span className="block text-xs text-subtle mt-1">{hint}</span>}
    </label>
  );
}

export function Select({ value, onChange, options, placeholder, className = "" }) {
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`control ${className}`}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => {
        const [v, l] = Array.isArray(o) ? o : [o, o];
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  );
}
